import type { Recibido } from "../api/tipos.ts";

/**
 * Empezar sin ayuda (SPEC-029 §15).
 *
 * El alta dependía de que alguien le dijera al cliente a qué dirección
 * reenviar, y de que alguien entrara a Postmark a leerle el código de Gmail.
 * Esto lo pone todo en una página.
 */

/**
 * El código con el que Gmail confirma un reenvío.
 *
 * Gmail lo manda A la dirección de 4drando, desde forwarding-noreply@google.com,
 * que no es un banco: 4drando lo rechaza. Pero el asunto de un rechazo se
 * guarda (SPEC-021), y Gmail pone el código al principio:
 * «(#123456789) Confirmación de reenvío de Gmail - Recibir correo de …». Así
 * que el código se puede leer sin abrir Postmark, que era la única salida.
 */
export function codigoGmail(correos: Recibido[]): { codigo: string; cuando: string } | null {
  const candidatos = correos
    .filter((c) => /forwarding-noreply@google\.com/i.test(c.remitente) || /gmail/i.test(c.asunto))
    .map((c) => ({ c, m: String(c.asunto).match(/\(#(\d{6,})\)/) }))
    .filter((x) => x.m)
    .sort((a, b) => Date.parse(b.c.recibido_en) - Date.parse(a.c.recibido_en));
  const primero = candidatos[0];
  return primero?.m?.[1] ? { codigo: primero.m[1], cuando: primero.c.recibido_en } : null;
}

export type Paso = "direccion" | "reenviador" | "prueba" | "avisos" | "cartera";

export interface EstadoInicio {
  direccion: string | null;
  reenviadores: string;
  correos: Recibido[];
  telegramVinculado: boolean;
  facturas: number;
}

/** Qué pasos están hechos. La dirección cuenta como hecha en cuanto existe. */
export function pasosHechos(e: EstadoInicio): Record<Paso, boolean> {
  return {
    direccion: !!e.direccion,
    reenviador: e.reenviadores.trim().length > 0,
    prueba: e.correos.some((c) => c.resultado === "REGISTRADO" || c.resultado === "DUPLICADO"),
    avisos: e.telegramVinculado,
    cartera: e.facturas > 0,
  };
}

/** El último correo que llegó, para decir qué pasó con la prueba. */
export const ultimoCorreo = (correos: Recibido[]) =>
  [...correos].sort((a, b) => Date.parse(b.recibido_en) - Date.parse(a.recibido_en))[0] ?? null;
